import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/contact/submit`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      content: 'hello@bbmagazine.com',
      link: 'mailto:hello@bbmagazine.com'
    },
    {
      icon: MapPin,
      title: 'Location',
      content: 'New York, NY',
      link: null
    },
    {
      icon: Phone,
      title: 'Editorial',
      content: 'editorial@bbmagazine.com',
      link: 'mailto:editorial@bbmagazine.com'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us · B&B Magazine</title>
        <meta name="description" content="Get in touch with B&B Magazine. Have a story idea, partnership proposal, or question? We'd love to hear from you." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com/contact" />
      </Helmet>

      <HeaderNav />

      <main>
        <section className="w-full py-16 md:py-24">
          <div className="container max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                Get In Touch
              </span>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
                Contact Us
              </h1>
              <p className="mt-4 text-lg text-muted-foreground max-w-[65ch] mx-auto">
                Have a story idea, partnership proposal, or question? We'd love to hear from you.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:col-span-2"
              >
                <Card className="p-6 md:p-8 bg-[hsl(var(--surface-2))] border-border">
                  <h2 className="font-display text-2xl font-bold mb-6">Send us a message</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="font-ui">Name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                          placeholder="Your name"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="font-ui">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                          placeholder="your@email.com"
                          className="mt-1.5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="subject" className="font-ui">Subject</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        required
                        placeholder="What's this about?"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="font-ui">Message</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        required
                        rows={6}
                        placeholder="Tell us more..."
                        className="mt-1.5"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full font-ui"
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </Card>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="space-y-6"
              >
                <Card className="p-6 bg-[hsl(var(--surface-2))] border-border">
                  <h3 className="font-display text-xl font-bold mb-4">Contact Information</h3>
                  <div className="space-y-4">
                    {contactInfo.map((info) => (
                      <div key={info.title} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--primary)/0.12)] border border-[hsl(var(--primary)/0.3)] flex items-center justify-center">
                          <info.icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-ui font-medium mb-0.5">{info.title}</p>
                          {info.link ? (
                            <a 
                              href={info.link} 
                              className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                            >
                              {info.content}
                            </a>
                          ) : (
                            <p className="text-sm text-muted-foreground">{info.content}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="p-6 bg-[hsl(var(--surface-2))] border-border">
                  <h3 className="font-display text-xl font-bold mb-3">Quick Links</h3>
                  <div className="space-y-2">
                    <a href="/advertise" className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                      → Advertising Opportunities
                    </a>
                    <a href="/about" className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                      → About Our Team
                    </a>
                    <a href="/" className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                      → Back to Homepage
                    </a>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}